import { useCallback, useEffect, useState } from 'react'
import { ActionIcon, Button, Checkbox, Stack, Text, TextInput, Tooltip } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconBan } from '@/components/icons'
import { AppModal } from '@/components/ui'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { notifyTicketsWorkspaceStale } from '@/lib/projects-events'
import { SPAM_SOURCE_LABELS } from '@/pages/settings/ProjectSpamPanel'
import type { TicketDetailTicket, TicketSpamState } from '@/types/api'

type Props = {
  ticket: TicketDetailTicket
  onChange: (state: TicketSpamState) => void
}

/** Иконка «Спам» / «Не спам» в шапке переписки + блокировка отправителя. */
export function TicketSpamControl({ ticket, onChange }: Props) {
  const [senderBlocked, setSenderBlocked] = useState(false)
  const [modal, setModal] = useState<'spam' | 'not-spam' | null>(null)
  const [blockSender, setBlockSender] = useState(true)
  const [unblockSender, setUnblockSender] = useState(true)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const isSpam = ticket.status === 'spam'

  const loadState = useCallback(async () => {
    try {
      const state = await api.ticketSpamState(ticket.id)
      setSenderBlocked(state.sender_blocked)
    } catch {
      setSenderBlocked(false)
    }
  }, [ticket.id])

  useEffect(() => {
    void loadState()
  }, [loadState, ticket.status])

  const submit = async () => {
    setBusy(true)
    try {
      const state =
        modal === 'spam'
          ? await api.ticketMarkSpam(ticket.id, { block_sender: blockSender, reason: reason.trim() || undefined })
          : await api.ticketNotSpam(ticket.id, { unblock_sender: unblockSender && senderBlocked })
      setSenderBlocked(state.sender_blocked)
      onChange(state)
      notifyTicketsWorkspaceStale()
      showNotification({
        title: 'Готово',
        message: modal === 'spam' ? 'Обращение помечено как спам.' : 'Обращение возвращено из спама.',
        color: 'green',
      })
      setModal(null)
      setReason('')
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      })
    } finally {
      setBusy(false)
    }
  }

  const spamLabel = isSpam
    ? `Спам${ticket.spam_source ? `: ${SPAM_SOURCE_LABELS[ticket.spam_source] || ticket.spam_source}` : ''}${
        ticket.spam_reason ? ` — ${ticket.spam_reason}` : ''
      }${senderBlocked ? '. Отправитель заблокирован' : ''}. Нажмите, чтобы вернуть из спама`
    : `Пометить как спам${senderBlocked ? ' (отправитель заблокирован)' : ''}`

  return (
    <>
      <Tooltip label={spamLabel} multiline maw={320}>
        <ActionIcon
          variant={isSpam ? 'light' : 'subtle'}
          color={isSpam || senderBlocked ? 'red' : undefined}
          size="sm"
          aria-label={isSpam ? 'Вернуть из спама' : 'Пометить как спам'}
          aria-pressed={isSpam}
          onClick={() => {
            if (isSpam) {
              setUnblockSender(true)
              setModal('not-spam')
            } else {
              setBlockSender(true)
              setModal('spam')
            }
          }}
        >
          <IconBan size={16} stroke={1.5} />
        </ActionIcon>
      </Tooltip>

      <AppModal
        opened={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'spam' ? 'Пометить обращение как спам?' : 'Вернуть обращение из спама?'}
        footer={
          <>
            <Button variant="default" onClick={() => setModal(null)}>
              Отмена
            </Button>
            <Button color={modal === 'spam' ? 'red' : undefined} loading={busy} onClick={() => void submit()}>
              {modal === 'spam' ? 'Это спам' : 'Не спам'}
            </Button>
          </>
        }
      >
        {modal === 'spam' ? (
          <Stack gap="sm">
            <Text size="sm">
              Обращение уйдёт в спам: Мозгобот и операторы его не получат. Сообщения и история сохранятся, решение
              можно отменить.
            </Text>
            <TextInput label="Причина" placeholder="Необязательно" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
            <Checkbox
              label="Заблокировать отправителя — его новые обращения сразу будут попадать в спам"
              checked={blockSender}
              onChange={(e) => setBlockSender(e.currentTarget.checked)}
            />
          </Stack>
        ) : (
          <Stack gap="sm">
            <Text size="sm">Обращение вернётся в общий поток: его снова обработает Мозгобот или получит оператор.</Text>
            {senderBlocked ? (
              <Checkbox
                label="Разблокировать отправителя"
                checked={unblockSender}
                onChange={(e) => setUnblockSender(e.currentTarget.checked)}
              />
            ) : null}
          </Stack>
        )}
      </AppModal>
    </>
  )
}
